import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ContactInfoModal } from '@/components/auth/ContactInfoModal';
import { useUpdateContactInfo } from '@/hooks/api/useUpdateContactInfo';
import { useAuthState } from '@/hooks/auth/useAuth';
import { useAvailableClients } from '@/hooks/api/useClients';
import { useGrantSmsConsent, useRevokeSmsConsent, useSmsConsentStatus } from '@/hooks/api/useSmsConsent';
import { getPhoneValidationError, getValidationErrorMessage } from '@/lib/api/errorHandler';
import { useState } from 'react';
import { createLogger } from '@/utils/logger';

const log = createLogger('ContactInfoPage');

export const Route = createFileRoute('/contact-info')({
  component: ContactInfoPage,
});

function ContactInfoPage() {
  const navigate = useNavigate();
  const { data: authState } = useAuthState();
  const { data: availableClients = [] } = useAvailableClients();
  const updateContactInfo = useUpdateContactInfo();
  const [error, setError] = useState<string | null>(null);

  // SMS consent hooks
  const { data: smsConsentStatus } = useSmsConsentStatus();
  const grantSmsConsent = useGrantSmsConsent();
  const revokeSmsConsent = useRevokeSmsConsent();

  // Format phone number from backend (10 digits) to display format (XXX) XXX-XXXX
  const formatPhoneNumber = (phone?: string): string | undefined => {
    if (!phone) return undefined;
    const digitsOnly = phone.replace(/\D/g, '');
    if (digitsOnly.length === 10) {
      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
    }
    return phone;
  };

  const handleSubmit = async (data: { email?: string; phoneNumber?: string; smsConsent?: boolean }) => {
    try {
      setError(null);

      // Remove formatting from phone number for API
      const phoneNumber = data.phoneNumber?.replace(/\D/g, '');

      // Use the dedicated contact info endpoint - only sends email and phone
      await updateContactInfo.mutateAsync({
        email: data.email,
        phoneNumber: phoneNumber,
      });

      // Handle SMS consent if phone number is provided
      if (phoneNumber && data.smsConsent !== undefined) {
        const currentlyHasConsent = smsConsentStatus?.hasActiveSmsConsent ?? false;

        if (data.smsConsent && !currentlyHasConsent) {
          // User opted in - grant consent
          log.info('Granting SMS consent');
          await grantSmsConsent.mutateAsync({
            consentMethod: 'WebPortal',
            acknowledgeSmsTerms: true,
          });
        } else if (!data.smsConsent && currentlyHasConsent) {
          // User opted out - revoke consent
          log.info('Revoking SMS consent');
          await revokeSmsConsent.mutateAsync({
            reason: 'User declined during contact info setup',
          });
        }
      }

      // Navigate to the appropriate destination based on client selection
      const storedClientId = sessionStorage.getItem('selectedClientId');
      const validStoredClientId = storedClientId && availableClients.some((client) => client.id === storedClientId);

      // If user has multiple clients and no valid client is selected, redirect to client selection
      if (availableClients.length > 1 && !validStoredClientId) {
        navigate({ to: '/client-select' });
      } else {
        // Single client or valid client already selected - go to home
        navigate({ to: '/' });
      }
    } catch (err) {
      // Check for phone validation errors first (from Twilio Lookup)
      const phoneError = getPhoneValidationError(err);
      if (phoneError) {
        setError(phoneError.message);
        return;
      }

      // Check for other validation errors from the API
      const validationError = getValidationErrorMessage(err);
      if (validationError) {
        setError(validationError);
        return;
      }

      // Generic error handling
      setError(err instanceof Error ? err.message : 'Failed to update contact information');
    }
  };

  return (
    <ContactInfoModal
      open={true}
      onSubmit={handleSubmit}
      isLoading={updateContactInfo.isPending || grantSmsConsent.isPending || revokeSmsConsent.isPending}
      error={error}
      currentEmail={authState?.user?.email}
      currentPhoneNumber={formatPhoneNumber(authState?.user?.phoneNumber)}
      hasSmsConsent={smsConsentStatus?.hasActiveSmsConsent ?? true}
    />
  );
}
